/**
 * Cloudinary URL/Image builder con selector de calidad.
 *
 * --- Migración Hetzner (READ path) ---
 *
 * Post-migración el backend deja de firmar URLs: expone `publicUrl` (URL pública
 * del objeto en Hetzner Object Storage) y, para imágenes legacy, el
 * `cloudinaryPublicId`. El frontend reconstruye la URL de entrega con el SDK
 * `@cloudinary/url-gen`:
 *
 *  - Hetzner: `cld.image(publicUrl).setDeliveryType('fetch')` — Cloudinary hace
 *    `/image/fetch/` sobre la URL pública (sin firma; lo permite "Allowed Fetch
 *    Domains"). "Restricted Fetched URL" queda desactivado.
 *  - Legacy: `cld.image(cloudinaryPublicId)` — entrega por public_id como
 *    siempre (`/image/upload/`).
 *
 * `buildCldImage` devuelve un `CloudinaryImage` listo para
 * `<AdvancedImage cldImg={...} />`. `resolveImageUrl` devuelve el string URL
 * (vía `.toURL()`) para los consumers que necesitan fetch()/descarga/ZIP/PDF.
 *
 * --- Calidad (Tarea #9) ---
 *
 * - `Quality.High` (`q_auto:best,f_auto`):
 *   Algoritmo conservador. Cloudinary baja típicamente al 88% del tamaño
 *   original. `f_auto` añade WebP/AVIF cuando el navegador lo soporta.
 *   Equivalente a la descarga "alta" para imprimir, archivar o adjuntar a
 *   documentación interna.
 *
 * - `Quality.Optimized` (`c_limit,w_1024,h_1024,q_auto:low,f_auto`):
 *   Variante muy agresiva para envío por mensajería/correo. Objetivo real
 *   ~100-300 KB.
 *
 *   CLAVE: `q_auto:*` solo recomprime; NO reduce la resolución. Las fotos de
 *   los operarios vienen del móvil a resolución completa (~12 MP), así que
 *   `q_auto:eco` por sí solo dejaba ficheros de 1-2 MB (bug observado: `best`
 *   y `eco` "se veían igual" porque ambos mantenían los 12 MP intactos).
 *
 *   `c_limit,w_1024,h_1024` define una caja CUADRADA: Cloudinary mete la
 *   imagen dentro respetando el aspect ratio y solo reduciendo. Efecto neto:
 *   el LADO MÁS LARGO se capa a 1024 px y el corto escala proporcional, sea
 *   horizontal o vertical (landscape → 1024×768, portrait → 768×1024). No se
 *   usa una caja asimétrica tipo `w_1280,h_720`: esa castiga al retrato (lo
 *   dejaba en 540×720, ~70 KB). Cloudinary aplica el factor de forma en
 *   servidor con las dimensiones reales del original; el front no las conoce
 *   (path Hetzner) ni le hace falta. Combinado con `q_auto:low`: validado
 *   visualmente, ~200-280 KB en ambas orientaciones con matrícula legible.
 *
 *   `Quality.High` se deja intencionadamente SIN cap de dimensión: impresión
 *   y archivo necesitan la resolución completa.
 *
 *   Los informes PDF embeben las fotos con `Quality.Optimized` (`size:'thumb'`,
 *   cap 1024 px, ~100-300 KB): decisión de cliente — calidad optimizada, no
 *   alta. Mantiene los PDFs ligeros para enviar/archivar.
 *
 * @see https://cloudinary.com/documentation/fetch_remote_images
 * @see https://cloudinary.com/documentation/image_optimization
 */
import { Cloudinary, CloudinaryImage } from '@cloudinary/url-gen';

export enum CloudinaryQuality {
  High = 'high',
  Optimized = 'optimized',
  /**
   * Variante para embeber en PDFs (@react-pdf/renderer). Igual de ligera que
   * `Optimized` (mismo cap 1024px + `q_auto:low`), pero fuerza **JPEG baseline**
   * (`f_jpg,fl_progressive:none`) en lugar de `f_auto` progresivo.
   *
   * Por qué: el decodificador JPEG de @react-pdf/renderer (jay-peg) NO decodifica
   * ciertos JPEG **progresivos** que Cloudinary entrega con `f_auto,q_auto:low`
   * — los descarta silenciosamente, así que el informe pierde fotos de forma
   * determinista (síntoma observado: 10 fotos válidas → solo 7 en el PDF). Además
   * `f_auto` puede servir WebP/AVIF al navegador, formatos que react-pdf tampoco
   * sabe embeber. `fl_progressive:none` (baseline) las hace todas decodificables
   * SIN penalización de tamaño (baseline ≈ progresivo a igual calidad). Validado:
   * 10/10 embebidas, mismo peso de PDF.
   */
  PdfOptimized = 'pdf-optimized',
}

/**
 * Descarga «optimizada» de una foto de Hetzner que CONSERVA el EXIF (GPS,
 * fecha de captura). Spec 2026-09-23 fotos geolocalizadas, § 17.
 *
 * Cloudinary borra los metadatos al transformar salvo con `fl_keep_iptc`, y
 * ese flag responde **HTTP 400** si va junto a `q_auto` («Quality auto cannot
 * be used in conjunction with keep_iptc»). Lo incompatible es la calidad
 * AUTOMÁTICA, no la optimización: con calidad fija sí los conserva (medido el
 * 2026-09-25 contra la nube de ROIS: 51 KB con EXIF frente a 46 KB sin él).
 *
 * `f_jpg` forzado: en WebP Cloudinary no escribe EXIF, y un JPEG lo abre
 * cualquier ordenador.
 *
 * Solo para DESCARGAR: una miniatura no necesita el GPS y pesaría más.
 */
export const DOWNLOAD_OPTIMIZED_KEEP_EXIF =
  'c_limit,w_1024,h_1024,q_60,f_jpg,fl_keep_iptc';

const QUALITY_TRANSFORMATIONS: Record<CloudinaryQuality, string> = {
  [CloudinaryQuality.High]: 'q_auto:best,f_auto',
  [CloudinaryQuality.Optimized]: 'c_limit,w_1024,h_1024,q_auto:low,f_auto',
  [CloudinaryQuality.PdfOptimized]:
    'c_limit,w_1024,h_1024,q_auto:low,f_jpg,fl_progressive:none',
};

/**
 * Instancia Cloudinary compartida (singleton). Misma config que el resto del
 * proyecto: `cloudName` desde `VITE_CLOUD_NAME`.
 */
export const cld = new Cloudinary({
  cloud: {
    cloudName: import.meta.env.VITE_CLOUD_NAME,
  },
});

/**
 * Shape mínimo de Image necesario para resolver su entrega.
 *
 * Las queries GraphQL que pinten/descarguen imágenes deben seleccionar:
 *  - `publicUrl` (URL pública Hetzner; null si legacy).
 *  - `cloudinaryPublicId` (legacy; null si Hetzner).
 *  - `storageProvider` (informativo).
 */
export interface CldResolvable {
  publicUrl?: string | null;
  cloudinaryPublicId?: string | null;
  storageProvider?: string | null;
}

// Compat: nombre histórico usado por consumers existentes.
export type ResolvableImage = CldResolvable;

/**
 * Devuelve un `CloudinaryImage` del SDK listo para
 * `<AdvancedImage cldImg={...} />` o `.toURL()`.
 *
 *  - Hetzner (`publicUrl` presente): fetch type sobre la URL pública.
 *  - Legacy (`cloudinaryPublicId`): por public_id como siempre.
 *
 * `cloudinary` por defecto a la instancia compartida; se acepta otra para casos
 * donde el `cloudName` deba ser distinto.
 */
export const buildCldImage = (
  image: CldResolvable | null | undefined,
  cloudinary: Cloudinary = cld,
): CloudinaryImage | null => {
  if (!image) return null;
  if (image.publicUrl) {
    return cloudinary.image(image.publicUrl).setDeliveryType('fetch');
  }
  if (image.cloudinaryPublicId) {
    return cloudinary.image(image.cloudinaryPublicId);
  }
  return null;
};

/**
 * URL del placeholder LQIP (blur-up). Variante DIMINUTA y de transformación
 * FIJA (`c_scale,w_32,q_auto,f_auto`) de la misma imagen.
 *
 * Por qué así (ver investigación en el PR): el plugin `placeholder()` de
 * @cloudinary/react difumina a resolución COMPLETA en servidor (lento,
 * ~2s de cómputo) y no se puede parametrizar. La técnica correcta "blur-up"
 * es entregar una imagen sharp minúscula (~cientos de bytes, cómputo Cloudinary
 * casi nulo) y difuminarla en el NAVEGADOR con CSS `filter: blur()`.
 *
 * La transformación es una sola y fija a propósito: Cloudinary cachea el
 * derivado y se factura UNA vez por imagen (no por variante — ver
 * cloudinary/frontend-frameworks#93).
 *
 * Reusa la lógica de entrega de `buildCldImage` (Hetzner `publicUrl` → fetch;
 * legacy `cloudinaryPublicId` → public_id). Devuelve `null` si no resoluble.
 */
export const buildLqipUrl = (
  image: CldResolvable | null | undefined,
  cloudinary: Cloudinary = cld,
): string | null => {
  const cldImg = buildCldImage(image, cloudinary);
  if (!cldImg) return null;
  cldImg.addTransformation('c_scale,w_32,q_auto,f_auto');
  return cldImg.toURL();
};

/**
 * Construye la URL de descarga de una imagen Cloudinary legacy a partir de su
 * `public_id`. Solo para sitios legacy que aún no disponen del objeto Image
 * completo. Los consumers nuevos deben preferir `resolveImageUrl`.
 */
export const buildCloudinaryDownloadUrl = (
  publicId: string,
  quality: CloudinaryQuality = CloudinaryQuality.High,
  cloudName: string = import.meta.env.VITE_CLOUD_NAME,
): string => {
  const transformation = QUALITY_TRANSFORMATIONS[quality];
  return `https://res.cloudinary.com/${cloudName}/image/upload/${transformation}/${publicId}`;
};

/**
 * Fuente única de verdad para la URL string de una imagen (descargas, fetch a
 * blob, ZIP, PDF). Construye el `CloudinaryImage` vía `buildCldImage` y aplica
 * la transformación de calidad antes de serializar con `.toURL()`.
 *
 * @param image      Objeto con `publicUrl` / `cloudinaryPublicId`.
 * @param size       'thumb' → calidad Optimized; 'full' → calidad High;
 *                   'pdf'   → calidad PdfOptimized (JPEG baseline para
 *                   @react-pdf/renderer, ver {@link CloudinaryQuality.PdfOptimized}).
 *                   (Mantiene la semántica histórica de los consumers que
 *                    pasaban 'thumb'|'full'.)
 * @param cloudinary Instancia opcional; por defecto la compartida.
 */
export const resolveImageUrl = (
  image: CldResolvable | null | undefined,
  size: 'thumb' | 'full' | 'pdf' = 'full',
  cloudinary: Cloudinary = cld,
): string | null => {
  const cldImg = buildCldImage(image, cloudinary);
  if (!cldImg) return null;
  const quality =
    size === 'pdf'
      ? CloudinaryQuality.PdfOptimized
      : size === 'thumb'
        ? CloudinaryQuality.Optimized
        : CloudinaryQuality.High;
  cldImg.addTransformation(QUALITY_TRANSFORMATIONS[quality]);
  return cldImg.toURL();
};
